import { Track } from "@/src/lib/tracking";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Member/SubscriptionMobilePricing/SubscriptionMobilePricing";
import React, { memo } from "react";
import { cn } from "utils";
import { Icon } from "@ospweb/sdk-ui";

import type { SubscriptionMobilePricingProps } from "./types";
import { useSubscriptionMobilePricingViewModel } from "./useSubscriptionMobilePricingViewModel";
import { SubscriptionMobilePricingFooter } from "./SubscriptionMobilePricingFooter";
import FeatureComparisonTableV2 from "@components/Member/Subscription/SubscriptionCardH5/CardList/FeatureComparisonTableV2";
import { AnimateNumber } from "@components/motions/AnimateNumber";

const I18n = I18nUtil.createScoped(Translations);

const logPricingPeriodSelect = (plan_id?: string) => {
  Track.event("pricing_period_select", { plan_id });
};

const SubscriptionMobilePricing: React.FC<SubscriptionMobilePricingProps> = ({
  isTrial,
  isLoading,
  trialList,
  data,
  userPlanInfo,
  onAppPurchaseSuccess,
  planIdParam,
}) => {
  const {
    selectedPlanType,
    setSelectedPlanType,
    displayedPricingOptions,
    shouldAnimatePriceOnTabSwitch,
    shouldShowMorePlans,
    shouldShowLessPlans,
    expandPlans,
    collapsePlans,
    selectedPlan,
    selectedBillingCycle,
    isPaying,
    handleStripeClick,
    ctaMode,
    ctaText,
    showCrypto,
  } = useSubscriptionMobilePricingViewModel({
    data,
    trialList,
    userPlanInfo,
    planIdParam,
  });

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden px-4 pt-4">
      <div className="min-h-0 flex-1 overflow-y-auto pb-4 no-scrollbar">
        <div className="flex flex-col gap-3">
          {displayedPricingOptions.map((option) => {
            const isSelected = option.key === selectedPlanType;
            const shouldAnimatePrice =
              shouldAnimatePriceOnTabSwitch &&
              Number.isFinite(option.priceAmountValue);
            const animatedPriceKey = `${option.key}-${option.priceAmountValue}`;

            return (
              <button
                key={animatedPriceKey}
                type="button"
                className={cn(
                  "w-full min-h-[64px] rounded-3 border-2 p-3 text-left transition-colors",
                  isSelected
                    ? "border-border_accent4_solid bg-background_accent4_overlay10"
                    : "border-border_white_transparent10 bg-background_solid_secondary",
                )}
                onClick={() => {
                  logPricingPeriodSelect(option.plan_id);
                  setSelectedPlanType(option.key);
                }}
              >
                <div className="flex flex-col gap-1">
                  {option.trialBadgeText ? (
                    <span className="inline-flex h-5 w-fit items-center rounded-1 bg-background_accent4_overlay100 px-1 body_medium_s text-content_inverse_primary mb-1">
                      {option.trialBadgeText}
                    </span>
                  ) : null}
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="headline_regular_s text-content_primary">
                        {option.title}
                      </div>
                      {option.saveText ? (
                        <div className="body_bold_s text-content_accent4_primary">
                          {option.saveText}
                        </div>
                      ) : option.secondaryText ? (
                        <div className="body_s text-content_tertiary">
                          {option.secondaryText}
                        </div>
                      ) : null}
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="headline_regular_s text-content_primary">
                        <span>{option.pricePrefixText}</span>
                        {shouldAnimatePrice ? (
                          <AnimateNumber
                            key={animatedPriceKey}
                            value={option.priceAmountValue}
                          />
                        ) : (
                          option.priceAmountText
                        )}
                        <span className="body_bold_m">
                          {option.priceUnitText}
                        </span>
                      </div>
                      {option.originalPriceText ? (
                        <div className="body_s text-content_tertiary">
                          {option.originalPriceText}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}

          {shouldShowMorePlans && (
            <button
              type="button"
              className="mx-auto mt-1 body_bold_m text-content_tertiary flex items-center gap-1"
              onClick={expandPlans}
            >
              {I18n.t("Show more plans")}
              <Icon name="chevron-down" className="w-5 h-5" size={20} />
            </button>
          )}
          {shouldShowLessPlans && (
            <button
              type="button"
              className="mx-auto mt-1 body_bold_m text-content_tertiary flex items-center gap-1"
              onClick={collapsePlans}
            >
              {I18n.t("Show less plans")}
              <Icon name="chevron-up" className="w-5 h-5" size={20} />
            </button>
          )}
        </div>

        <FeatureComparisonTableV2 className="mt-8" />
      </div>

      <SubscriptionMobilePricingFooter
        ctaMode={ctaMode}
        ctaText={ctaText}
        selectedPlan={selectedPlan}
        selectedBillingCycle={selectedBillingCycle}
        isLoading={isLoading}
        isPaying={isPaying}
        isTrial={isTrial}
        trialList={trialList}
        userPlanInfo={userPlanInfo}
        onStripeClick={handleStripeClick}
        onAppPurchaseSuccess={onAppPurchaseSuccess}
        showCrypto={showCrypto}
      />
    </div>
  );
};

export default memo(SubscriptionMobilePricing);
